import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import useTechCopy from "./useTechCopy";
import BrandSwitch from "../components/BrandSwitch";
import OnHumanTechLogo from "./logos/OnHumanTechLogo";
import LangSwitch from "../hr/components/LangSwitch";
import { HRLocaleContext } from "../hr/i18n/HRLocaleProvider";
import { useBrandTransition } from "../components/BrandTransitionProvider";

// Page chrome for OnHuman.tech: fixed nav + footer.
// The brand switch lives in the same container, at the same size and spot, as
// on the HR site, so it stays put when the brand veil sweeps between domains.
// Only the right-hand side of the bar is styled for this site: a pill of
// section links plus the language toggle from xl up (the same breakpoint the
// HR bar uses for its links), a menu button below that.
export default function TechLayout({ children }) {
  const copy = useTechCopy();
  const { locale } = useContext(HRLocaleContext);
  const { startHRTransition } = useBrandTransition();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const close = () => setOpen(false);

  // Transparent over the video hero; picks up a dark blur once the page
  // content starts passing underneath.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { href: "#capabilities", label: copy.nav.capabilities },
    { href: "#process", label: copy.nav.process },
    { href: "#domains", label: copy.nav.domains },
  ];

  const pillLink =
    "px-3.5 py-1.5 rounded-full text-sm text-white/80 hover:text-white transition-colors duration-300";
  const drawerLink =
    "flex items-center py-4 border-b border-white/10 text-base text-white/80 hover:text-white transition-colors duration-300";
  const arrowCls =
    "h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5";

  return (
    // The lang attribute follows the shared locale so CSS text-transform uses
    // the right casing rules (Turkish dotted İ vs. English I).
    <div
      lang={locale}
      className="tech-root min-h-screen bg-black text-white font-inter antialiased selection:bg-white/20 selection:text-white"
    >
      {/* Nav */}
      <header
        className={
          scrolled || open
            ? "fixed top-0 left-0 right-0 z-40 bg-black/70 backdrop-blur-md transition-colors duration-300"
            : "fixed top-0 left-0 right-0 z-40 bg-transparent transition-colors duration-300"
        }
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-10 lg:px-16 h-20 flex items-center justify-between gap-3 sm:gap-6">
          <div className="min-w-0">
            <BrandSwitch active="tech" onHRNavigate={startHRTransition} />
          </div>

          <div className="hidden xl:flex items-center gap-5">
            <nav
              aria-label="Primary"
              className="flex items-center gap-1 rounded-full border border-gray-700 px-2 py-1.5"
            >
              {navItems.map((item) => (
                <a key={item.href} href={item.href} className={pillLink}>
                  {item.label}
                </a>
              ))}
              <a href="#contact" className={`group inline-flex items-center gap-1 ${pillLink}`}>
                {copy.nav.contact}
                <ArrowUpRight className={arrowCls} aria-hidden="true" />
              </a>
            </nav>
            <LangSwitch tone="tech" className="text-sm" />
          </div>

          <button
            type="button"
            className="xl:hidden p-2 -mr-2 text-white/80 hover:text-white transition-colors duration-300"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>

        {/* Drawer: ruled list of sections, then one row of utilities. Mirrors
            the HR drawer's structure in this site's own palette. */}
        {open && (
          <div className="xl:hidden border-t border-white/10 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <nav aria-label="Mobile" className="px-6 py-2">
              {navItems.map((item) => (
                <a key={item.href} href={item.href} onClick={close} className={drawerLink}>
                  {item.label}
                </a>
              ))}
              <a href="#contact" onClick={close} className={`group ${drawerLink} border-b-0 gap-1`}>
                {copy.nav.contact}
                <ArrowUpRight className={arrowCls} aria-hidden="true" />
              </a>
            </nav>
            <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between gap-4">
              <LangSwitch tone="tech" className="text-sm" />
              <Link
                to="/anamenu"
                onClick={(e) => { e.preventDefault(); close(); startHRTransition(); }}
                className="group inline-flex items-center gap-1 text-sm text-white/80 hover:text-white transition-colors duration-300"
              >
                {copy.brand.hrLinkLabel}
                <ArrowUpRight className={arrowCls} aria-hidden="true" />
              </Link>
            </div>
          </div>
        )}
      </header>

      <main id="top" className="relative z-10">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative z-10 bg-black border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 py-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <OnHumanTechLogo className="h-8 w-auto" />
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/60">{copy.footer.blurb}</p>
            </div>
            <nav className="md:col-span-4 grid grid-cols-2 gap-3 content-start text-sm text-white/60">
              {navItems.map((item) => (
                <a key={item.href} href={item.href} className="hover:text-white transition-colors duration-300">
                  {item.label}
                </a>
              ))}
              <a href="#contact" className="hover:text-white transition-colors duration-300">
                {copy.nav.contact}
              </a>
              <Link
                to="/anamenu"
                onClick={(e) => { e.preventDefault(); startHRTransition(); }}
                className="group inline-flex items-center gap-1 hover:text-white transition-colors duration-300"
              >
                {copy.brand.hrLinkLabel}
                <ArrowUpRight className={arrowCls} aria-hidden="true" />
              </Link>
            </nav>
            <div className="md:col-span-3 text-sm">
              <div className="text-white/50">{copy.footer.emailLabel}</div>
              <a
                href={`mailto:${copy.cta.email}`}
                className="mt-1 block text-white hover:text-white/70 transition-colors duration-300"
              >
                {copy.cta.email}
              </a>
            </div>
          </div>
          <div className="pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/40">
            <span>© {new Date().getFullYear()} {copy.brand.name}. {copy.footer.rights}</span>
            <span>{copy.footer.location}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
